import type { Schemas } from '@npc/api-client'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import type { CopyId } from '@npc/copy'

import { api } from '../../app/api'
import { call } from '../../app/errors'

export type Channel = Schemas['NotificationRule']['channel']

/**
 * 投递档位的梯子，从最安静到最吵。
 *
 * **顺序来自契约**（`openapi.yaml` 的 `NotificationRule.channel` 枚举
 * `[none, digest, push, phone]`），不是这儿排的。前端只是把它写下来，
 * 因为 TS 的联合类型在运行期没有顺序。
 *
 * 它**不参与运行期判断**——降档走下面那张 `DOWNGRADE` 表。
 * 留着它是给用例当第二个来源：用例拿它核对「表里每一条真的只降了一档、
 * 且恰好覆盖地板以上那几档」。判据与用例同源的话，
 * 两边永远对得上，也永远发现不了错。
 */
export const LADDER = ['none', 'digest', 'push', 'phone'] as const satisfies readonly Channel[]

/**
 * 编译期哨兵：契约里加一个档位而这儿没跟上，`_AllChannelsOnTheLadder`
 * 立刻解析成 `never`，下面那行赋值编不过。
 *
 * 写成哨兵而不是注释，是因为「两边必须一起改」的约定写在注释里没有任何
 * 约束力——需要动手的人读不到它（CLAUDE.md §8）。
 */
type _AllChannelsOnTheLadder =
  Exclude<Channel, (typeof LADDER)[number]> extends never ? true : never
const _ladderCoversTheContract: _AllChannelsOnTheLadder = true
void _ladderCoversTheContract

/**
 * 「以后这种别叫醒我」的落点：当前档位 → 降到哪一档 + 那句按钮文案。
 *
 * # 地板就是这张表，**只有这一处**
 *
 * 表里没有的档位（`digest`、`none`）没有降档动作。
 *
 * 第一版把地板写了两遍——`quieterThan` 里一句 `next === 'none'` 判断，
 * 加上文案表恰好没有 `none` 这一项。于是变异验证时把那句判断删掉，
 * 界面用例**全绿**：另一份实现替它兜住了。那正是 CLAUDE.md §8
 * 「同一条规则有两份实现时，变异验证很可能验的是没改的那一份」。
 *
 * # 为什么地板在 `digest`
 *
 * 契约把这件事写得很直白：
 *
 * > 它调的是 `channel` 不是 `enabled`。「别叫醒我」的意思是降一档投递，
 * > 不是把这条规则整个关掉——关掉之后那类问题连早报里都不出现了，
 * > 而用户要的只是别在半夜响。
 *
 * 同一条理由对 `digest → none` 一字不差地成立：`none` 是「不打扰」，
 * 也就是关掉的另一种写法。
 *
 * # 为什么落点与文案在同一行
 *
 * 分成两张表的话，梯子改了而文案表没跟上时，按钮会说「发微信」
 * 却把档位调到别处，而两边都不会红。
 */
const DOWNGRADE = {
  phone: { to: 'push', copy: 'alert.rules.stop_calling' },
  push: { to: 'digest', copy: 'alert.rules.wait_for_digest' },
} as const satisfies Partial<Record<Channel, { to: Channel; copy: CopyId }>>

/** 地板：这一档以下（含这一档）不再有降档动作。用例拿它与 DOWNGRADE 对账。 */
export const FLOOR = 'digest' as const satisfies Channel

/**
 * 这一行该降到哪一档、按钮上写什么；不该有按钮时回 `null`。
 *
 * 认不出的档位（后端加了新的一档而前端还没重新生成客户端）也回 `null`——
 * 猜一个「大概比它低一档」的值，等于替用户改了一个我们看不懂的设置。
 */
export function downgradeAction(channel: Channel): { to: Channel; copy: CopyId } | null {
  return (DOWNGRADE as Partial<Record<string, { to: Channel; copy: CopyId }>>)[channel] ?? null
}

/**
 * 改一条规则走哪条路。
 *
 * # 为什么只发 `channel`
 *
 * 契约里两个字段都可选，而后端只写收到的那几个（`COALESCE` 上去）。
 * 顺手带一个 `enabled` 过去，会把用户上一次自己关掉的那条规则悄悄打开——
 * 而两次请求都回 200。
 *
 * # `ruleId` 一定来自 `GET /notification-rules` 的出参
 *
 * **不许在前端写死任何 rule id。** 那四条内置规则的 id 住在服务端
 * （`alertapi.builtinRules`：`site_down` / `needs_you` / `handled` / `night_noise`），
 * 而 MSW 的夹具用的是另一套（`service_down` / `needs_decision` /
 * `handled` / `night_minor`）。写死一侧的 id，就是在另一侧 404——
 * 正是 `tools/check-contract-implemented.mjs` 存在的那种「dev 绿、线上 404」。
 */
export function useDowngradeNotificationRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { ruleId: string; channel: Channel }) =>
      call('/notification-rules/{ruleId}', () =>
        api.PATCH('/notification-rules/{ruleId}', {
          params: { path: { ruleId: input.ruleId } },
          body: { channel: input.channel },
        }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notification-rules'] })
    },
  })
}
